import React from 'react';
import { 
  LayoutDashboard, Users, FileText, BarChart3, Settings, 
  UserCheck, Clock, CheckCircle, TrendingUp, Bell, Search,
  PlusCircle, Filter, Eye, Edit, Trash2, Download, Upload,
  Building2, GraduationCap, DollarSign, MessageSquare, Award, Sparkles
} from 'lucide-react';

// Reusable slide content components
const FeatureList = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3">
        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
        <span className="text-gray-600">{item}</span>
      </li>
    ))}
  </ul>
);

const StepList = ({ steps }) => (
  <div className="space-y-4">
    {steps.map((step, i) => (
      <div key={i} className="flex items-start gap-4">
        <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center text-gray-800 font-semibold flex-shrink-0">
          {i + 1}
        </div>
        <div>
          <p className="font-medium text-gray-800">{step.title}</p>
          {step.description && <p className="text-sm text-gray-500 mt-1">{step.description}</p>}
        </div>
      </div>
    ))}
  </div>
);

const KeyboardShortcuts = ({ shortcuts }) => (
  <div className="grid grid-cols-2 gap-3">
    {shortcuts.map((item, i) => (
      <div key={i} className="flex items-center gap-3 bg-gray-100 rounded-lg p-3">
        <kbd className="px-2 py-1 bg-gray-200 rounded text-sm font-mono text-teal-600">{item.key}</kbd>
        <span className="text-gray-600 text-sm">{item.action}</span>
      </div>
    ))}
  </div>
);

const StatCard = ({ icon, label, value, color = 'yellow' }) => (
  <div className="bg-gray-100 rounded-xl p-4 flex items-center gap-3">
    <div className={`p-2 rounded-lg bg-${color}-500/20`}>
      {icon}
    </div>
    <div>
      <p className="text-2xl font-semibold text-gray-800">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  </div>
);

export const adminTrainingSlides = [
  // Slide 1: Welcome
  {
    title: "Welcome to Admin Dashboard",
    subtitle: "Your central hub for managing H E Direct operations",
    category: "Getting Started",
    icon: <LayoutDashboard className="w-8 h-8 text-teal-600" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-teal-50 to-cyan-50",
    content: (
      <div className="space-y-6">
        <p className="text-lg text-gray-600">
          As an administrator, you have full control over the platform. This training will guide you through:
        </p>
        <div className="grid grid-cols-2 gap-4">
          <StatCard icon={<Users className="w-5 h-5 text-blue-500" />} label="Manage Students" value="75+" />
          <StatCard icon={<Building2 className="w-5 h-5 text-green-500" />} label="Partner Agents" value="27+" />
          <StatCard icon={<FileText className="w-5 h-5 text-purple-500" />} label="Applications" value="22+" />
          <StatCard icon={<GraduationCap className="w-5 h-5 text-orange-500" />} label="Courses" value="688+" />
        </div>
      </div>
    ),
    tips: "Press F to go fullscreen for the best experience!"
  },

  // Slide 2: Dashboard Overview
  {
    title: "Dashboard Overview",
    subtitle: "Understanding your main statistics",
    category: "Navigation",
    icon: <BarChart3 className="w-8 h-8 text-blue-500" />,
    iconBg: "bg-blue-100",
    bgColor: "bg-gradient-to-r from-blue-500/10 to-cyan-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">The top of your dashboard shows key metrics at a glance:</p>
        <div className="grid grid-cols-5 gap-3">
          {[
            { label: "Students", icon: "👨‍🎓", desc: "Total registered" },
            { label: "Agents", icon: "🤝", desc: "Partner accounts" },
            { label: "Applications", icon: "📄", desc: "All submissions" },
            { label: "Pending", icon: "⏳", desc: "Awaiting review" },
            { label: "Accepted", icon: "✅", desc: "Successful apps" },
          ].map((item, i) => (
            <div key={i} className="bg-gray-100 rounded-xl p-4 text-center">
              <span className="text-2xl">{item.icon}</span>
              <p className="font-medium text-gray-800 mt-2">{item.label}</p>
              <p className="text-xs text-gray-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    ),
    tips: "Click on any stat card to see detailed information!"
  },

  // Slide 3: Quick Actions
  {
    title: "Quick Actions Panel",
    subtitle: "Handle urgent items immediately",
    category: "Workflow",
    icon: <Clock className="w-8 h-8 text-red-500" />,
    iconBg: "bg-red-100",
    bgColor: "bg-gradient-to-r from-red-500/10 to-pink-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">The Quick Actions panel shows items requiring immediate attention:</p>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gray-100 rounded-xl p-4 border-l-4 border-yellow-500">
            <UserCheck className="w-6 h-6 text-teal-600 mb-2" />
            <p className="font-medium text-gray-800">Agent Approvals</p>
            <p className="text-sm text-gray-500">New registrations waiting for review</p>
          </div>
          <div className="bg-gray-100 rounded-xl p-4 border-l-4 border-orange-500">
            <FileText className="w-6 h-6 text-orange-500 mb-2" />
            <p className="font-medium text-gray-800">Awaiting Review</p>
            <p className="text-sm text-gray-500">Applications needing attention</p>
          </div>
          <div className="bg-gray-100 rounded-xl p-4 border-l-4 border-green-500">
            <DollarSign className="w-6 h-6 text-green-500 mb-2" />
            <p className="font-medium text-gray-800">Payout Requests</p>
            <p className="text-sm text-gray-500">Commission withdrawals pending</p>
          </div>
        </div>
      </div>
    ),
    tips: "Check Quick Actions first thing every morning!"
  },

  // Slide 4: Journey Tracking System
  {
    title: "Student Journey Tracking",
    subtitle: "12-stage application pipeline management",
    category: "Core Feature",
    icon: <TrendingUp className="w-8 h-8 text-green-500" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-500/10 to-emerald-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Track every application through 12 distinct stages:</p>
        <div className="grid grid-cols-4 gap-2 text-sm">
          {[
            "1. Inquiry", "2. Lead Validated", "3. App Received", "4. App Submitted",
            "5. Offer Issued", "6. Offer Accepted", "7. UCO", "8. Deposit",
            "9. CAS Stage", "10. Visa Issued", "11. Enrolled", "12. Commission"
          ].map((stage, i) => (
            <div key={i} className={`p-2 rounded-lg text-center ${i < 4 ? 'bg-blue-100 text-blue-600' : i < 8 ? 'bg-teal-100 text-teal-600' : 'bg-green-100 text-green-600'}`}>
              {stage}
            </div>
          ))}
        </div>
      </div>
    ),
    tips: "Click on any stage in the funnel to filter applications by that stage."
  },

  // Slide 5: Updating Journey Stages
  {
    title: "Updating Journey Stages",
    subtitle: "How to progress applications through the pipeline",
    category: "Core Feature",
    icon: <Edit className="w-8 h-8 text-purple-500" />,
    iconBg: "bg-purple-100",
    bgColor: "bg-gradient-to-r from-purple-500/10 to-pink-500/10",
    content: (
      <div className="space-y-6">
        <StepList steps={[
          { title: "Go to Applications tab", description: "View the clean list of all applications" },
          { title: "Click on any application row", description: "Or click the 'View' button to open details" },
          { title: "View Journey Tracker at top", description: "The full 12-stage journey is prominently displayed" },
          { title: "Use status dropdown to update", description: "Change status directly from the detail page" },
          { title: "Add notes for the team", description: "Internal notes or messages to students" }
        ]} />
        <div className="mt-4 p-3 bg-teal-50 rounded-lg border border-teal-200">
          <p className="text-sm text-teal-700">💡 <strong>New:</strong> Applications now open in a dedicated detail page with the Journey Tracker at the top for easier tracking!</p>
        </div>
      </div>
    ),
    tips: "Always add notes when changing stages - it helps track the application history."
  },

  // Slide 6: Journey Analytics
  {
    title: "Journey Analytics Dashboard",
    subtitle: "Visualize your conversion funnel",
    category: "Analytics",
    icon: <BarChart3 className="w-8 h-8 text-cyan-500" />,
    iconBg: "bg-cyan-100",
    bgColor: "bg-gradient-to-r from-cyan-500/10 to-blue-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">The Journey Analytics tab provides powerful insights:</p>
        <FeatureList items={[
          "Clickable funnel chart - click any stage to filter applications",
          "Conversion rates between consecutive stages",
          "Average time applications spend at each stage",
          "Agent performance rankings by completion rate",
          "Overall enrollment and offer acceptance rates"
        ]} />
      </div>
    ),
    tips: "Use the funnel to identify bottlenecks - low conversion rates indicate problem areas."
  },

  // Slide 7: Agent Management
  {
    title: "Managing Agents",
    subtitle: "Approve, reject, and assign POCs",
    category: "User Management",
    icon: <Users className="w-8 h-8 text-orange-500" />,
    iconBg: "bg-orange-100",
    bgColor: "bg-gradient-to-r from-orange-500/10 to-amber-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Agent management workflow:</p>
        <StepList steps={[
          { title: "Review pending agents", description: "Check company details and certifications" },
          { title: "Approve or Reject", description: "Rejected agents receive reason via email" },
          { title: "Assign Point of Contact", description: "Select an employee as their dedicated contact" },
          { title: "Monitor performance", description: "Track referrals and completion rates" }
        ]} />
      </div>
    ),
    tips: "Assigning a POC improves agent satisfaction and communication."
  },

  // Slide 8: Adding Content
  {
    title: "Managing Universities & Courses",
    subtitle: "Add, edit, and import content",
    category: "Content Management",
    icon: <GraduationCap className="w-8 h-8 text-indigo-500" />,
    iconBg: "bg-indigo-500/20",
    bgColor: "bg-gradient-to-r from-indigo-500/10 to-violet-500/10",
    content: (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-teal-600" /> Universities
            </h3>
            <FeatureList items={[
              "Add new partner universities",
              "Set country and ranking",
              "Delete removes all courses too"
            ]} />
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-teal-600" /> Courses
            </h3>
            <FeatureList items={[
              "Add courses with full details",
              "Import via CSV for bulk upload",
              "Search and filter easily"
            ]} />
          </div>
        </div>
      </div>
    ),
    tips: "Use CSV import to add multiple courses at once - download the template first!"
  },

  // Slide 9: Insights & Announcements
  {
    title: "Creating Insights",
    subtitle: "Broadcast announcements to users",
    category: "Communication",
    icon: <Bell className="w-8 h-8 text-pink-500" />,
    iconBg: "bg-pink-100",
    bgColor: "bg-gradient-to-r from-pink-500/10 to-rose-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Create announcements for students, agents, or everyone:</p>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gray-100 rounded-xl p-4 border border-gray-200">
            <p className="font-medium text-gray-800 mb-2">Normal</p>
            <p className="text-sm text-gray-500">General updates and information</p>
          </div>
          <div className="bg-teal-500/10 rounded-xl p-4 border border-teal-200">
            <p className="font-medium text-teal-600 mb-2">Important</p>
            <p className="text-sm text-gray-500">Key information to highlight</p>
          </div>
          <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
            <p className="font-medium text-red-500 mb-2">Urgent</p>
            <p className="text-sm text-gray-500">Critical alerts requiring action</p>
          </div>
        </div>
      </div>
    ),
    tips: "Set expiry dates for time-sensitive announcements to keep the feed clean."
  },

  // Slide 10: Scholarship Management
  {
    title: "Scholarship Management",
    subtitle: "Add and manage scholarship opportunities",
    category: "Scholarships",
    icon: <Award className="w-8 h-8 text-teal-600" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-yellow-500/10 to-amber-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Help students find funding through the Scholarships tab:</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-100 rounded-xl p-4 border-l-4 border-yellow-500">
            <PlusCircle className="w-6 h-6 text-teal-600 mb-2" />
            <p className="font-medium text-gray-800">Add Scholarship</p>
            <p className="text-sm text-gray-500">Manually add scholarships like GREAT, Chevening, etc.</p>
          </div>
          <div className="bg-gray-100 rounded-xl p-4 border-l-4 border-purple-500">
            <Sparkles className="w-6 h-6 text-purple-500 mb-2" />
            <p className="font-medium text-gray-800">AI Fetch</p>
            <p className="text-sm text-gray-500">Use AI to discover scholarships (e.g., Free study in Italy)</p>
          </div>
        </div>
        <StepList steps={[
          { title: "Go to Scholarships Tab", description: "Click on the Scholarships tab in the navigation" },
          { title: "Add Manually or Use AI", description: "Click Add Scholarship or AI Fetch button" },
          { title: "Fill Details", description: "Enter name, provider, countries, funding type, deadline" },
          { title: "Review Student Inquiries", description: "Check Admin > Scholarship Interests for student applications" }
        ]} />
      </div>
    ),
    tips: "AI Fetch works great for queries like 'German scholarships for masters' or 'PhD funding in Canada'"
  },

  // Slide 11: Search Feature
  {
    title: "Search Students & Agents",
    subtitle: "Quickly find users by name or ID",
    category: "New Feature",
    icon: <Search className="w-8 h-8 text-cyan-500" />,
    iconBg: "bg-cyan-100",
    bgColor: "bg-gradient-to-r from-cyan-500/10 to-blue-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Use the search bar to quickly find students or agents:</p>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" /> Students Tab
            </h3>
            <FeatureList items={[
              "Search by student name",
              "Search by email address",
              "Search by student ID",
              "Search by phone number"
            ]} />
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-500" /> Agents Tab
            </h3>
            <FeatureList items={[
              "Search by agent name",
              "Search by email address",
              "Search by company name",
              "Search by agent ID"
            ]} />
          </div>
        </div>
        <div className="mt-4 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
          <p className="text-sm text-cyan-700">💡 Results update instantly as you type. Click the X button to clear search.</p>
        </div>
      </div>
    ),
    tips: "The search shows 'Showing X of Y' count to help you know how many results match."
  },

  // Slide: Task Management
  {
    title: "Task Management",
    subtitle: "Assign and track tasks for your team",
    category: "New Feature",
    icon: <MessageSquare className="w-8 h-8 text-purple-500" />,
    iconBg: "bg-purple-100",
    bgColor: "bg-gradient-to-r from-purple-500/10 to-pink-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">The Tasks tab lets you assign work to team members:</p>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-800">Creating Tasks</h3>
            <FeatureList items={[
              "Click 'Assign Task' button",
              "Enter title and description",
              "Select team member to assign",
              "Set priority (Low to Urgent)",
              "Add due date if needed"
            ]} />
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-800">Task Features</h3>
            <FeatureList items={[
              "Priority color coding (urgent = red)",
              "Status tracking (pending/in progress/completed)",
              "Email notifications sent automatically",
              "Team members see tasks on their dashboard",
              "Mark complete with one click"
            ]} />
          </div>
        </div>
      </div>
    ),
    tips: "Use 'Send Notification' to broadcast announcements to all users or specific roles."
  },

  // Slide: Attendance Tracking
  {
    title: "Employee Attendance",
    subtitle: "Track team check-ins and work hours",
    category: "New Feature",
    icon: <Clock className="w-8 h-8 text-blue-500" />,
    iconBg: "bg-blue-100",
    bgColor: "bg-gradient-to-r from-blue-500/10 to-indigo-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">The Attendance tab shows real-time team presence:</p>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <StatCard icon={<Users className="w-5 h-5 text-blue-500" />} label="Total Employees" value="--" />
          <StatCard icon={<CheckCircle className="w-5 h-5 text-green-500" />} label="Checked In" value="--" color="green" />
          <StatCard icon={<Clock className="w-5 h-5 text-red-500" />} label="Absent" value="--" color="red" />
        </div>
        <FeatureList items={[
          "View who is checked in today",
          "Location verification (Office vs Remote)",
          "Check-in and check-out times",
          "Monthly attendance reports available",
          "Employees check in from their 'My Attendance' tab"
        ]} />
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
          <p className="text-sm font-medium text-blue-800 mb-2">📍 Office Hours Configuration</p>
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
            <p>• Working Days: Mon-Sat</p>
            <p>• Hours: 10 AM - 6 PM PKT</p>
            <p>• Peshawar Office: 200m radius</p>
            <p>• Islamabad Office: 200m radius</p>
          </div>
        </div>
      </div>
    ),
    tips: "Employees must be within 200m of office to be marked as 'Present'. Late arrivals (after 10:30 AM) are tracked."
  },

  // Slide: Notification Bell
  {
    title: "Notifications Center",
    subtitle: "Stay updated with the notification bell",
    category: "New Feature",
    icon: <Bell className="w-8 h-8 text-orange-500" />,
    iconBg: "bg-orange-100",
    bgColor: "bg-gradient-to-r from-orange-500/10 to-amber-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">The notification bell in your header keeps you informed:</p>
        <FeatureList items={[
          "Red badge shows unread notification count",
          "Click bell to see recent notifications",
          "Notifications from task assignments appear here",
          "System alerts and updates displayed",
          "Mark all as read with one click",
          "Click individual notification to mark as read"
        ]} />
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
            <p className="text-sm font-medium text-orange-800">Sending Notifications</p>
            <p className="text-xs text-orange-600 mt-1">Use the Send Notification button in Tasks tab to broadcast messages to all users or specific roles.</p>
          </div>
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm font-medium text-green-800">Email Option</p>
            <p className="text-xs text-green-600 mt-1">Check the email notification option to also deliver via email.</p>
          </div>
        </div>
      </div>
    ),
    tips: "Task completions also trigger notifications to keep admins informed."
  },

  // Slide: Source Filters
  {
    title: "Source Type Filters",
    subtitle: "Filter students and applications by source",
    category: "New Feature",
    icon: <Filter className="w-8 h-8 text-teal-500" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-teal-500/10 to-cyan-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Easily distinguish between direct and agent-referred students:</p>
        <div className="grid grid-cols-2 gap-6">
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2">Direct (B2C)</h3>
            <p className="text-sm text-blue-600">Students who registered directly on the platform without agent referral.</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-2">Via Agent (B2B)</h3>
            <p className="text-sm text-purple-600">Students referred by partner agents, tracked for commission.</p>
          </div>
        </div>
        <FeatureList items={[
          "Use 'All Sources' dropdown in Applications tab",
          "Filter shows Direct (B2C) or Via Agent (B2B)",
          "Helps track agent performance and referrals",
          "Available on both Students and Applications"
        ]} />
      </div>
    ),
    tips: "Click on an agent to see all their referred students and applications."
  },

  // Slide: University Commission Management
  {
    title: "University Commission Management",
    subtitle: "Set and manage commission rates per university",
    category: "New Feature",
    icon: <DollarSign className="w-8 h-8 text-green-500" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-500/10 to-emerald-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Manage the 70/30 revenue split for each university:</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
            <p className="font-medium text-green-700 mb-2">Agent Share (70%)</p>
            <p className="text-sm text-gray-600">Percentage paid to referring agent</p>
          </div>
          <div className="bg-orange-500/10 rounded-xl p-4 border border-orange-500/30">
            <p className="font-medium text-orange-700 mb-2">H E Direct (30%)</p>
            <p className="text-sm text-gray-600">Percentage retained by company</p>
          </div>
        </div>
        <StepList steps={[
          { title: "Go to Universities tab", description: "View all partner universities" },
          { title: "Click Edit on any university", description: "Opens the edit modal" },
          { title: "Set Commission % and Agent Share", description: "E.g., 15% commission, 70% to agent" },
          { title: "Configure Available Intakes", description: "Select which months are open for applications" }
        ]} />
      </div>
    ),
    tips: "Use bulk selection to update commission for multiple universities at once!"
  },

  // Slide: Apply for Students (NEW FEATURE)
  {
    title: "Apply for Courses on Behalf of Students",
    subtitle: "Submit applications for any student directly",
    category: "New Feature",
    icon: <GraduationCap className="w-8 h-8 text-teal-600" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-teal-50 to-cyan-50",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Admins and employees can now apply for courses on behalf of students:</p>
        <StepList steps={[
          { title: "Go to Students tab", description: "View all registered students" },
          { title: "Click 'Apply' button", description: "Next to any student in the Actions column" },
          { title: "Search for courses", description: "Filter by name, university, or field of study" },
          { title: "Click 'Apply' on a course", description: "Application is created immediately" }
        ]} />
        <div className="mt-4 p-3 bg-teal-50 rounded-lg border border-teal-200">
          <p className="text-sm text-teal-700">💡 The system prevents duplicate applications - you'll get an error if the student already applied for that course.</p>
        </div>
      </div>
    ),
    tips: "This feature is great for walk-in students or phone inquiries where you need to apply quickly!"
  },

  // Slide: Edit University Country (NEW FEATURE)
  {
    title: "Edit University Details & Country",
    subtitle: "Fix country labels and update university info",
    category: "New Feature",
    icon: <Edit className="w-8 h-8 text-purple-500" />,
    iconBg: "bg-purple-100",
    bgColor: "bg-gradient-to-r from-purple-500/10 to-pink-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">You can now edit university details including the country:</p>
        <StepList steps={[
          { title: "Go to Universities tab", description: "View all partner universities" },
          { title: "Click the pencil icon", description: "On any university card" },
          { title: "Edit details", description: "Name, country, city, ranking, commission" },
          { title: "Save changes", description: "All courses are automatically updated!" }
        ]} />
        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
          <p className="text-sm text-green-700">✅ When you change a university's country, all its courses are automatically updated to the new country.</p>
        </div>
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-sm text-amber-700">⚠️ Use this to fix Irish universities incorrectly showing as UK - no need to run DB tools!</p>
        </div>
      </div>
    ),
    tips: "This is the sustainable fix for the Ireland/UK data issue - edit directly instead of running scripts."
  },

  // Slide: Country Filter
  {
    title: "Country Filter for Universities",
    subtitle: "Filter universities by destination country",
    category: "New Feature",
    icon: <Filter className="w-8 h-8 text-blue-500" />,
    iconBg: "bg-blue-100",
    bgColor: "bg-gradient-to-r from-blue-500/10 to-cyan-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Quickly find universities by country:</p>
        <FeatureList items={[
          "Use the country dropdown filter in Universities tab",
          "Shows count per country (e.g., 'United Kingdom (30)')",
          "Select All works with filtered results",
          "Bulk commission can be applied to filtered selection",
          "UK universities show April/May 2026 intakes only"
        ]} />
        <div className="grid grid-cols-3 gap-3 mt-4">
          {['United Kingdom', 'Canada', 'Germany', 'USA', 'France', 'Ireland'].map(country => (
            <div key={country} className="bg-gray-100 rounded-lg p-3 text-center text-sm">
              {country}
            </div>
          ))}
        </div>
      </div>
    ),
    tips: "Filter by UK to see all universities with April/May 2026 intakes."
  },

  // Slide: Bulk Student Upload
  {
    title: "Bulk Student Upload",
    subtitle: "Upload multiple students via CSV",
    category: "New Feature",
    icon: <Upload className="w-8 h-8 text-purple-500" />,
    iconBg: "bg-purple-100",
    bgColor: "bg-gradient-to-r from-purple-500/10 to-pink-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Save time by uploading multiple students at once:</p>
        <StepList steps={[
          { title: "Click 'Bulk Upload' in Students tab", description: "Opens the upload modal" },
          { title: "Download the CSV template", description: "Contains all required column headers" },
          { title: "Fill in student details", description: "full_name and email are required" },
          { title: "Upload and preview", description: "Review before confirming" },
          { title: "Check results", description: "See success/failure counts and temp passwords" }
        ]} />
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-4">
          <p className="text-sm text-purple-700">💡 Tip: For multiple countries, use quotes in CSV: &quot;UK, Canada, Germany&quot;</p>
        </div>
      </div>
    ),
    tips: "Temporary passwords are generated and shown for each successfully uploaded student."
  },

  // Slide: Counselling Fee Management
  {
    title: "Counselling Fee Management",
    subtitle: "Track service fees for Direct (B2C) students",
    category: "New Feature",
    icon: <DollarSign className="w-8 h-8 text-teal-600" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-teal-50 to-cyan-50",
    content: (
      <div className="space-y-6">
        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
          <p className="font-medium text-blue-800 mb-2">Important</p>
          <p className="text-sm text-blue-600">Fee management applies ONLY to Direct (B2C) students, NOT agent-referred students.</p>
        </div>
        <FeatureList items={[
          "Set fee rules by destination country",
          "View student Finance Tab showing payments",
          "Record payments with method and notes",
          "Track outstanding balances",
          "Staff Saving Pot credits for large payments",
          "Set monthly collection targets for staff"
        ]} />
      </div>
    ),
    tips: "Agents handle their own fee collection - this is for H E Direct's direct services only."
  },

  // Slide 12: Summary
  {
    title: "You're Ready!",
    subtitle: "Key takeaways and daily checklist",
    category: "Summary",
    icon: <CheckCircle className="w-8 h-8 text-green-500" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-500/10 to-emerald-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Daily admin checklist:</p>
        <FeatureList items={[
          "Check Quick Actions for urgent items",
          "Review pending agent approvals",
          "Process applications awaiting review",
          "Handle payout requests",
          "Review Journey Analytics for bottlenecks",
          "Check scholarship inquiries from students",
          "Use Search to quickly find specific users",
          "Review staff targets and saving pots"
        ]} />
        <div className="mt-6 p-4 bg-teal-50 rounded-xl border border-teal-200">
          <p className="text-teal-600 font-medium">📞 Need Help?</p>
          <p className="text-gray-600 text-sm mt-1">Contact: WhatsApp +92 326 8096371 | Email: admin.team@hedirect.org</p>
        </div>
      </div>
    ),
    tips: "You can restart this training anytime from the Training Hub!"
  },

  // === NEW FEATURES (2026) ===
  // Slide: AI Visa Success Predictor
  {
    title: "AI Visa Success Predictor",
    subtitle: "GPT-powered approval probability for every applicant",
    category: "New • AI",
    icon: <Sparkles className="w-8 h-8 text-indigo-600" />,
    iconBg: "bg-indigo-100",
    bgColor: "bg-gradient-to-r from-indigo-50 to-violet-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          The Visa Probability Checker analyses each application against destination policies,
          academic profile, finances and language scores — then returns an approval likelihood
          with AI-generated advisory notes.
        </p>
        <FeatureList items={[
          "Open any Application → 'Visa Probability' panel",
          "See success score (0–100%) + risk band",
          "Review GPT-generated counsellor notes & action items",
          "Track historical outcomes in Admin → Outcomes tab"
        ]} />
        <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-200">
          <p className="text-sm text-indigo-700"><strong>Tip:</strong> Use the Outcomes tab to spot patterns by destination, university or course.</p>
        </div>
      </div>
    ),
    tips: "Predictions run on GPT-5.2. They are advisory, not guarantees."
  },

  // Slide: High-Risk Triage / Needs Review
  {
    title: "Needs Review — High-Risk Triage",
    subtitle: "Auto-flag at-risk applications for counsellor review",
    category: "New • AI",
    icon: <Bell className="w-8 h-8 text-rose-600" />,
    iconBg: "bg-rose-100",
    bgColor: "bg-gradient-to-r from-rose-50 to-orange-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          Any prediction scoring below 60% or hitting a critical flag automatically tags
          the application as <strong>Needs Review</strong> and pings the assigned counsellor.
        </p>
        <FeatureList items={[
          "Red 'Needs Review' badge on the Applications table",
          "Filter the table by 'Needs Review only'",
          "Counsellors receive an in-app notification",
          "Add a counsellor note to clear the flag"
        ]} />
      </div>
    ),
    tips: "Clear high-risk flags after addressing the concern — keep your queue clean."
  },

  // Slide: Smart Matching Engine
  {
    title: "Smart Course Matching",
    subtitle: "Academic % + IELTS equivalence baked in",
    category: "Enhanced",
    icon: <Award className="w-8 h-8 text-emerald-600" />,
    iconBg: "bg-emerald-100",
    bgColor: "bg-gradient-to-r from-emerald-50 to-teal-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          Course recommendations now factor in expanded academic percentages and English
          test equivalence (IELTS / PTE / Duolingo / TOEFL) for realistic admit odds.
        </p>
        <FeatureList items={[
          "Add Matric, Inter & Bachelors % to student profile",
          "System maps any English score → IELTS equivalent",
          "Recommendations rank by realistic admission odds",
          "'Why not' reasons available on hover for rejected matches"
        ]} />
      </div>
    ),
    tips: "Encourage employees & agents to fill the full academic profile — better data = better matches."
  },

  // Slide: Document Approval Workflow
  {
    title: "Inline Document Approval",
    subtitle: "Approve / reject student docs without page switching",
    category: "Workflow",
    icon: <CheckCircle className="w-8 h-8 text-green-600" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-50 to-emerald-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          Student & journey documents now ship with inline <strong>Approve</strong> /
          <strong> Reject</strong> buttons. A "Documents Required" banner highlights what's
          still missing per destination.
        </p>
        <FeatureList items={[
          "Open Student → Documents or Application → Journey Docs",
          "Click Approve to mark the doc verified",
          "Click Reject with a reason; student/agent gets notified",
          "Banner ticks off requirements automatically"
        ]} />
      </div>
    ),
    tips: "Use rejection reasons — they appear in the student/agent notification email."
  },

  // Slide: Record Payment & Bulk CSV Imports
  {
    title: "Record Payments + Bulk Imports",
    subtitle: "Faster admin tooling",
    category: "Tools",
    icon: <Upload className="w-8 h-8 text-blue-600" />,
    iconBg: "bg-blue-100",
    bgColor: "bg-gradient-to-r from-blue-50 to-cyan-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">Two productivity wins for admins:</p>
        <FeatureList items={[
          "Student → Payments → 'Record Payment' to log fees with auto-allocation",
          "Courses & Universities tabs → 'Bulk CSV Upload' for catalog updates",
          "Payment confirmation emails fire automatically to the payer",
          "Invalid CSV rows are rejected with line-by-line feedback"
        ]} />
      </div>
    ),
    tips: "Always download the latest CSV template before bulk upload — schema may evolve."
  },

  // Slide: System Health Tab
  {
    title: "System Health Tab",
    subtitle: "At-a-glance platform observability",
    category: "Admin Ops",
    icon: <BarChart3 className="w-8 h-8 text-teal-600" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-teal-50 to-cyan-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          The Admin Dashboard now exposes a System Health tab with live metrics:
        </p>
        <FeatureList items={[
          "API uptime, latency and request volume",
          "Object Storage availability + last document upload time",
          "Last successful daily DB backup timestamp",
          "Rate-limit hits on auth endpoints"
        ]} />
        <div className="p-4 bg-teal-50 rounded-xl border border-teal-200">
          <p className="text-sm text-teal-700"><strong>Red status?</strong> Contact Engineering immediately — don't troubleshoot in prod.</p>
        </div>
      </div>
    ),
    tips: "Check System Health once a day. Two minutes saves a midnight incident."
  }
];

export default adminTrainingSlides;
